import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CredentialCard from './components/CredentialCard';
import CredentialDetailPanel from './components/CredentialDetailPanel';
import FilterPanel from './components/FilterPanel';
import BulkActionsBar from './components/BulkActionsBar';
import WalletStats from './components/WalletStats';
import SecuritySection from './components/SecuritySection';

const DigitalWallet = () => {
  const navigate = useNavigate();
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
    sortBy: 'newest',
    dateRange: {
      from: '',
      to: ''
    },
    showExpiringSoon: false,
    showBlockchainAnchored: false,
    showRecentlyAdded: false
  });

  // Mock credentials data
  const mockCredentials = [
    {
      id: 'cred-001',
      title: 'Bachelor of Computer Science',
      type: 'education',
      status: 'verified',
      issuer: {
        name: 'Stanford University',
        logo: 'https://images.unsplash.com/photo-1562774053-701939374585?w=100&h=100&fit=crop&crop=center',
        description: 'Leading research university in California',
        website: 'stanford.edu'
      },
      issuedDate: '2023-06-15T10:00:00Z',
      expirationDate: null,
      lastVerified: '2025-01-15T14:30:00Z',
      claims: [
        { name: 'Degree', value: 'Bachelor of Computer Science' },
        { name: 'Major', value: 'Artificial Intelligence' },
        { name: 'GPA', value: '3.8/4.0' },
        { name: 'Graduation Date', value: 'June 15, 2023' }
      ],
      schemaVersion: '1.0.0',
      format: 'JSON-LD',
      encoding: 'UTF-8',
      metadata: {
        credentialSubject: 'John Doe',
        degreeType: 'Bachelor',
        honors: 'Magna Cum Laude'
      },
      signatureAlgorithm: 'Ed25519',
      hashAlgorithm: 'SHA-256',
      digitalSignature: 'z58DAdFfa9CwfDrOeab2yEH2CGC3dkeCzDHADBA9aTHkwCsNc6BtLdJc6wJQ2CuFVmLBg3WMfdGGBtKHdcePaYW2Q',
      cryptographicProof: true,
      blockchain: {
        network: 'Ethereum',
        txHash: '0x1234567890abcdef1234567890abcdef12345678',
        blockNumber: 18500000
      },
      verificationHistory: [
        {
          type: 'issued',
          action: 'Credential Issued',
          description: 'Credential was issued by Stanford University',
          timestamp: '2023-06-15T10:00:00Z'
        },
        {
          type: 'verified',
          action: 'Credential Verified',
          description: 'Verified by TechCorp during job application',
          timestamp: '2024-03-20T09:15:00Z'
        },
        {
          type: 'shared',
          action: 'Credential Shared',
          description: 'Shared with LinkedIn for profile verification',
          timestamp: '2024-08-10T16:45:00Z'
        }
      ]
    },
    {
      id: 'cred-002',
      title: 'AWS Solutions Architect',
      type: 'certification',
      status: 'verified',
      issuer: {
        name: 'Amazon Web Services',
        logo: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop&crop=center',
        description: 'Cloud computing platform by Amazon',
        website: 'aws.amazon.com'
      },
      issuedDate: '2024-09-01T08:00:00Z',
      expirationDate: '2027-09-01T08:00:00Z',
      lastVerified: '2025-01-20T11:20:00Z',
      claims: [
        { name: 'Certification', value: 'AWS Solutions Architect - Professional' },
        { name: 'Level', value: 'Professional' },
        { name: 'Score', value: '850/1000' },
        { name: 'Exam Code', value: 'SAP-C02' }
      ],
      schemaVersion: '2.1.0',
      format: 'JWT',
      encoding: 'UTF-8',
      metadata: {
        examLocation: 'Online Proctored',
        validationCode: 'AWS-SAP-2024-001'
      },
      signatureAlgorithm: 'RS256',
      hashAlgorithm: 'SHA-256',
      digitalSignature: 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ',
      cryptographicProof: true,
      blockchain: {
        network: 'Polygon',
        txHash: '0xabcdef1234567890abcdef1234567890abcdef12',
        blockNumber: 45600000
      },
      verificationHistory: [
        {
          type: 'issued',
          action: 'Certification Issued',
          description: 'AWS certification successfully completed',
          timestamp: '2024-09-01T08:00:00Z'
        },
        {
          type: 'verified',
          action: 'Credential Verified',
          description: 'Verified by CloudTech Solutions',
          timestamp: '2024-12-15T14:30:00Z'
        }
      ]
    },
    {
      id: 'cred-003',
      title: 'Professional Driver License',
      type: 'license',
      status: 'expired',
      issuer: {
        name: 'California DMV',
        logo: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=100&h=100&fit=crop&crop=center',
        description: 'California Department of Motor Vehicles',
        website: 'dmv.ca.gov'
      },
      issuedDate: '2020-03-15T09:00:00Z',
      expirationDate: '2024-03-15T09:00:00Z',
      lastVerified: '2024-03-10T10:15:00Z',
      claims: [
        { name: 'License Type', value: 'Class A Commercial' },
        { name: 'Endorsements', value: 'Hazmat, Passenger' },
        { name: 'License Number', value: 'CA-CDL-123456789' },
        { name: 'Restrictions', value: 'None' }
      ],
      schemaVersion: '1.5.0',
      format: 'JSON-LD',
      encoding: 'UTF-8',
      metadata: {
        issuingState: 'California',
        licenseClass: 'A'
      },
      signatureAlgorithm: 'ECDSA',
      hashAlgorithm: 'SHA-256',
      digitalSignature: 'r1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef12s1234567890abcdef',
      cryptographicProof: true,
      blockchain: {
        network: 'Hyperledger Fabric',
        txHash: 'tx-hyperledger-001234567890',
        blockNumber: 1250000
      },
      verificationHistory: [
        {
          type: 'issued',
          action: 'License Issued',
          description: 'Commercial driver license issued by CA DMV',
          timestamp: '2020-03-15T09:00:00Z'
        },
        {
          type: 'verified',
          action: 'License Verified',
          description: 'Verified by TransLogistics Inc.',
          timestamp: '2023-11-20T13:45:00Z'
        }
      ]
    },
    {
      id: 'cred-004',
      title: 'Medical License',
      type: 'professional',
      status: 'verified',
      issuer: {
        name: 'Medical Board of California',
        logo: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=100&h=100&fit=crop&crop=center',
        description: 'State medical licensing authority',
        website: 'mbc.ca.gov'
      },
      issuedDate: '2022-07-01T12:00:00Z',
      expirationDate: '2026-07-01T12:00:00Z',
      lastVerified: '2025-01-25T16:00:00Z',
      claims: [
        { name: 'License Type', value: 'Physician and Surgeon' },
        { name: 'Specialty', value: 'Internal Medicine' },
        { name: 'License Number', value: 'CA-MD-987654321' },
        { name: 'Board Certified', value: 'Yes' }
      ],
      schemaVersion: '2.0.0',
      format: 'JSON-LD',
      encoding: 'UTF-8',
      metadata: {
        medicalSchool: 'UCSF School of Medicine',
        residencyProgram: 'Internal Medicine - UCSF'
      },
      signatureAlgorithm: 'Ed25519',
      hashAlgorithm: 'SHA-256',
      digitalSignature: 'z58DAdFfa9CwfDrOeab2yEH2CGC3dkeCzDHADBA9aTHkwCsNc6BtLdJc6wJQ2CuFVmLBg3WMfdGGBtKHdcePaYW2Q',
      cryptographicProof: true,
      blockchain: {
        network: 'Ethereum',
        txHash: '0xfedcba0987654321fedcba0987654321fedcba09',
        blockNumber: 18750000
      },
      verificationHistory: [
        {
          type: 'issued',
          action: 'Medical License Issued',
          description: 'Medical license granted after board examination',
          timestamp: '2022-07-01T12:00:00Z'
        },
        {
          type: 'verified',
          action: 'License Verified',
          description: 'Verified by St. Mary\'s Hospital',
          timestamp: '2024-11-30T08:20:00Z'
        }
      ]
    },
    {
      id: 'cred-005',
      title: 'Project Management Professional',
      type: 'certification',
      status: 'pending',
      issuer: {
        name: 'Project Management Institute',
        logo: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=100&h=100&fit=crop&crop=center',
        description: 'Global association for project management',
        website: 'pmi.org'
      },
      issuedDate: '2025-01-30T14:00:00Z',
      expirationDate: '2028-01-30T14:00:00Z',
      lastVerified: '2025-01-30T14:00:00Z',
      claims: [
        { name: 'Certification', value: 'Project Management Professional (PMP)' },
        { name: 'Credential ID', value: 'PMP-2025-001234' },
        { name: 'PDUs Required', value: '60 every 3 years' },
        { name: 'Exam Score', value: 'Above Target' }
      ],
      schemaVersion: '1.8.0',
      format: 'JSON-LD',
      encoding: 'UTF-8',
      metadata: {
        examDate: '2025-01-25',
        applicationApproved: '2025-01-20'
      },
      signatureAlgorithm: 'Ed25519',
      hashAlgorithm: 'SHA-256',
      digitalSignature: 'pending_signature_validation',
      cryptographicProof: false,
      blockchain: null,
      verificationHistory: [
        {
          type: 'issued',
          action: 'Certification Pending',
          description: 'PMP certification issued, pending final verification',
          timestamp: '2025-01-30T14:00:00Z'
        }
      ]
    }
  ];

  const [credentials, setCredentials] = useState(mockCredentials);

  // Filter and sort credentials
  const getFilteredCredentials = () => {
    let filtered = [...credentials];

    // Search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(cred => 
        cred?.title?.toLowerCase()?.includes(searchTerm) ||
        cred?.issuer?.name?.toLowerCase()?.includes(searchTerm) ||
        cred?.type?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Type filter
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(cred => cred?.type === filters?.type);
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(cred => cred?.status === filters?.status);
    }

    // Date range filter
    if (filters?.dateRange?.from || filters?.dateRange?.to) {
      filtered = filtered?.filter(cred => {
        const issuedDate = new Date(cred.issuedDate);
        const fromDate = filters?.dateRange?.from ? new Date(filters.dateRange.from) : null;
        const toDate = filters?.dateRange?.to ? new Date(filters.dateRange.to) : null;
        
        if (fromDate && issuedDate < fromDate) return false;
        if (toDate && issuedDate > toDate) return false;
        return true;
      });
    }

    // Quick filters
    if (filters?.showExpiringSoon) {
      filtered = filtered?.filter(cred => {
        if (!cred?.expirationDate) return false;
        const expirationDate = new Date(cred.expirationDate);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow?.setDate(thirtyDaysFromNow?.getDate() + 30);
        return expirationDate <= thirtyDaysFromNow && expirationDate > new Date();
      });
    }

    if (filters?.showBlockchainAnchored) {
      filtered = filtered?.filter(cred => cred?.blockchain);
    }

    if (filters?.showRecentlyAdded) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
      filtered = filtered?.filter(cred => new Date(cred.issuedDate) >= sevenDaysAgo);
    }

    // Sort
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'newest':
          return new Date(b.issuedDate) - new Date(a.issuedDate);
        case 'oldest':
          return new Date(a.issuedDate) - new Date(b.issuedDate);
        case 'title':
          return a?.title?.localeCompare(b?.title);
        case 'issuer':
          return a?.issuer?.name?.localeCompare(b?.issuer?.name);
        case 'expiring':
          if (!a?.expirationDate && !b?.expirationDate) return 0;
          if (!a?.expirationDate) return 1;
          if (!b?.expirationDate) return -1;
          return new Date(a.expirationDate) - new Date(b.expirationDate);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredCredentials = getFilteredCredentials();

  // Handle credential selection
  const handleCredentialSelect = (credentialId, isSelected) => {
    if (isSelected) {
      setSelectedCredentials(prev => [...prev, credentialId]);
    } else {
      setSelectedCredentials(prev => prev?.filter(id => id !== credentialId));
    }
  };

  const handleSelectAll = () => {
    if (selectedCredentials?.length === filteredCredentials?.length) {
      setSelectedCredentials([]);
    } else {
      setSelectedCredentials(filteredCredentials?.map(cred => cred?.id));
    }
  };

  // Handle credential actions
  const handleViewCredential = (credential) => {
    setSelectedCredential(credential);
  };

  const handleShareCredential = (credential) => {
    console.log('Sharing credential:', credential?.id);
    // Navigate to sharing interface or open modal
  };

  const handleDownloadCredential = (credential) => {
    console.log('Downloading credential:', credential?.id);
    // Implement download functionality
  };

  const handleRevokeCredential = (credential) => {
    console.log('Revoking credential:', credential?.id);
    // Implement revocation functionality
  };

  // Handle bulk actions
  const handleBulkShare = async () => {
    console.log('Bulk sharing credentials:', selectedCredentials);
    setSelectedCredentials([]);
  };

  const handleBulkDownload = async () => {
    console.log('Bulk downloading credentials:', selectedCredentials);
    setSelectedCredentials([]);
  };

  const handleBulkRevoke = async () => {
    console.log('Bulk revoking credentials:', selectedCredentials);
    setSelectedCredentials([]);
  };

  const handleBulkBackup = async () => {
    console.log('Bulk backing up credentials:', selectedCredentials);
    setSelectedCredentials([]);
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      status: 'all',
      sortBy: 'newest',
      dateRange: { from: '', to: '' },
      showExpiringSoon: false,
      showBlockchainAnchored: false,
      showRecentlyAdded: false
    });
  };

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Filter Panel */}
          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            isOpen={isFilterPanelOpen}
            onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            className="w-80 flex-shrink-0 hidden lg:block"
          />

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header Section */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Digital Wallet</h1>
                  <p className="text-muted-foreground mt-1">
                    Manage your verifiable credentials securely
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => handleNavigation('/credential-issuance')}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add Credential
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => console.log('Backup wallet')}
                    iconName="Archive"
                    iconPosition="left"
                  >
                    Backup Wallet
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <WalletStats credentials={credentials} className="mb-6" />

              {/* Security Section */}
              <SecuritySection className="mb-6" />

              {/* Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedCredentials?.length === filteredCredentials?.length && filteredCredentials?.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm text-muted-foreground">
                      Select All ({filteredCredentials?.length})
                    </span>
                  </div>
                  
                  {/* Mobile Filter Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                    className="lg:hidden"
                    iconName="Filter"
                    iconPosition="left"
                  >
                    Filters
                  </Button>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Icon name="Grid3X3" size={16} />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <Icon name="List" size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Credentials Grid/List */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-2">
                    <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                    <span className="text-muted-foreground">Loading credentials...</span>
                  </div>
                </div>
              ) : filteredCredentials?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                    <Icon name="FileText" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No credentials found</h3>
                  <p className="text-muted-foreground mb-4">
                    {filters?.search || filters?.type !== 'all' || filters?.status !== 'all' ?'Try adjusting your filters to see more results.' :'Start by adding your first verifiable credential.'}
                  </p>
                  <Button
                    variant="default"
                    onClick={() => handleNavigation('/credential-issuance')}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add First Credential
                  </Button>
                </div>
              ) : (
                <div className={
                  viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' :'space-y-4'
                }>
                  {filteredCredentials?.map((credential) => (
                    <CredentialCard
                      key={credential?.id}
                      credential={credential}
                      isSelected={selectedCredentials?.includes(credential?.id)}
                      onSelect={handleCredentialSelect}
                      onView={handleViewCredential}
                      onShare={handleShareCredential}
                      className={viewMode === 'list' ? 'max-w-none' : ''}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          {selectedCredential && (
            <CredentialDetailPanel
              credential={selectedCredential}
              onClose={() => setSelectedCredential(null)}
              onShare={handleShareCredential}
              onDownload={handleDownloadCredential}
              onRevoke={handleRevokeCredential}
            />
          )}
        </div>

        {/* Mobile Filter Panel */}
        {isFilterPanelOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setIsFilterPanelOpen(false)} />
            <div className="fixed inset-y-0 left-0 w-80 bg-background border-r border-border overflow-y-auto">
              <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                isOpen={true}
                onToggle={() => setIsFilterPanelOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        <BulkActionsBar
          selectedCredentials={selectedCredentials}
          onClearSelection={() => setSelectedCredentials([])}
          onBulkShare={handleBulkShare}
          onBulkDownload={handleBulkDownload}
          onBulkRevoke={handleBulkRevoke}
          onBulkBackup={handleBulkBackup}
        />
      </div>
    </div>
  );
};

export default DigitalWallet;